import {
  calendarDateSchema,
  compareDocStatus,
  normalizeDocName,
  type StoredMediaFile,
  StoredMediaFileSchema,
} from "@repo/types";
import { type CalendarDate, toCalendarDate, toDate } from "@repo/utils/date";
import * as z from "zod";
import DRIVER_DOCS from "@/components/Delivery/utils/driver-docs-constants";
import type { GetDriverByIdQuery } from "@/libs/generated/operations";

export const VehicleNumberEnum = z.enum([
  "LESS_THAN_3",
  "BETWEEN_3_5",
  "BETWEEN_5_10",
  "LESS_THAN_10",
  "BETWEEN_10_20",
  "BETWEEN_20_50",
  "MORE_THAN_10",
  "MORE_THAN_50",
]);

export const KycLevel = z.enum(["LIGHT", "REGULAR"]);

export const UboDeclarationStatus = z.enum([
  "CREATED",
  "VALIDATION_ASKED",
  "VALIDATED",
  "INCOMPLETE",
  "REFUSED",
]);

export const SubscriptionStatus = z.enum([
  "NONE",
  "PENDING",
  "FAILED",
  "CANCELLED",
  "ACTIVE",
  "EXPIRED",
]);

export const BusinessStatus = z.enum(["COMPANY", "DRIVER"]);

/** Calendar dates are edited as `YYYY-MM-DD`; "" when the field is cleared. */
const optionalCalendarDate = calendarDateSchema.or(z.literal("")).optional();

const toOptionalCalendarDate = (v?: string | Date | null): CalendarDate | undefined => {
  const date = toDate(v);
  return date ? toCalendarDate(date) : undefined;
};

function buildDocDefault(
  doc: (typeof DRIVER_DOCS)[number],
  existingDoc: StoredMediaFile | undefined
) {
  return {
    ...doc,
    url: existingDoc?.url ?? null,
    key: existingDoc?.key || "",
    docId: existingDoc?.docId ?? existingDoc?.id,
    tempObjectUrl: existingDoc?.tempObjectUrl || "",
    version_status: existingDoc?.version_status,
    document_state: existingDoc?.document_state,
    rejectionReason: existingDoc?.rejectionReason,
    additionalInfo: existingDoc?.additionalInfo,
    handledBy: existingDoc?.handledBy,
    updatedAt: existingDoc?.updatedAt || "",
    expiresAt: existingDoc?.expiresAt,
    metadata: {
      id:
        typeof existingDoc?.metadata?.id === "string" ||
        typeof existingDoc?.metadata?.id === "number"
          ? String(existingDoc.metadata.id)
          : doc.id,
    },
  };
}

export const DriverFormSchema = z.object({
  id: z.number(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.email().optional(),
  phoneNumber: z.string().optional(),
  birthDate: optionalCalendarDate,
  drivingLicenseDate: optionalCalendarDate,
  drivingPermitDueDate: optionalCalendarDate,
  drivingPermitNumber: z.string().optional(),
  business_status: BusinessStatus.optional(),
  iban: z.string().optional(),
  company: z.string().optional(),
  siret: z.string().optional(),
  truck_ownership_status: z.boolean().optional(),
  wgarage: z.boolean().optional(),
  insurance_expiration_date: optionalCalendarDate,
  vigilance_issue_date: optionalCalendarDate,
  kbis_issue_date: optionalCalendarDate,
  address: z
    .object({
      id: z.number().nullable().optional(),
      address: z.string().nullable().optional(),
      city: z.string().nullable().optional(),
      postalCode: z.string().nullable().optional(),
      addressPrecision: z.string().nullable().optional(),
    })
    .optional(),
  docs: z
    .array(
      StoredMediaFileSchema.extend({
        metadata: z.object({ id: z.string() }).optional(),
      })
    )
    .optional(),
});

export type DriverFormSchemaType = z.infer<typeof DriverFormSchema>;

export function GetDriverFormDefaultValues(
  driverData?: GetDriverByIdQuery["getDriverById"]
): DriverFormSchemaType {
  const docs = (driverData?.docs as StoredMediaFile[] | undefined) ?? [];
  const docMap = new Map<string, StoredMediaFile>();
  for (const d of docs) {
    const rawName = (d.metadata?.id as string | undefined) || d.label || d.name || d.key || "";
    const key = normalizeDocName(rawName);
    const existing = docMap.get(key);

    if (!existing || compareDocStatus(d, existing) < 0) {
      docMap.set(key, d);
    }
  }

  return {
    id: driverData?.id ?? 0,
    firstName: driverData?.firstName || "",
    lastName: driverData?.lastName || "",
    email: driverData?.email || "",
    phoneNumber: driverData?.phoneNumber || "",
    birthDate: toOptionalCalendarDate(driverData?.birthDate),
    drivingLicenseDate: toOptionalCalendarDate(driverData?.drivingLicenseDate),
    drivingPermitDueDate: toOptionalCalendarDate(driverData?.drivingPermitDueDate),
    drivingPermitNumber: driverData?.drivingPermitNumber || "",
    business_status: driverData?.business_status ?? undefined,
    iban: driverData?.iban || "",
    company: driverData?.company || "",
    siret: driverData?.siret || "",
    truck_ownership_status: driverData?.truck_ownership_status ?? undefined,
    wgarage: driverData?.wgarage ?? undefined,
    insurance_expiration_date: toOptionalCalendarDate(driverData?.insurance_expiration_date),
    vigilance_issue_date: toOptionalCalendarDate(driverData?.vigilance_issue_date),
    kbis_issue_date: toOptionalCalendarDate(driverData?.kbis_issue_date),
    address: {
      id: driverData?.address?.id ?? undefined,
      address: driverData?.address?.address || "",
      city: driverData?.address?.city || "",
      postalCode: driverData?.address?.postalCode || "",
      addressPrecision: driverData?.address?.addressPrecision || "",
    },
    docs: DRIVER_DOCS.map((doc) => buildDocDefault(doc, docMap.get(normalizeDocName(doc.key)))),
  };
}
